import { chatServerInstance, getBookingStatusAndStyles } from "@/helpers";
import { usePayment } from "@/providers";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { FaCreditCard } from "react-icons/fa6";
import { PiGreaterThanLight } from "react-icons/pi";
import Swal from "sweetalert2";

const BookingDetailsTabs = ({ booking, arrival, departure, diffDays, isConfirm, isDeclined, isHost, handleConfirmBooking, expenses, setExpenses, openDiscount, setOpenDiscount, setOPenPost }) => {
    const [activeTab, setActiveTab] = useState("reservationDates");
    const [showModal, setShowModal] = useState(false);
    const [declined, setIsDeclined] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [acceptedExpenses, setAcceptedExpenses] = useState([]);
    const [rejectedExpenses, setRejectedExpenses] = useState([]);
    const { paymentMethod, getPaymentMethods } = usePayment();
    const user = JSON.parse(localStorage.getItem("data"));
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [partialModal, setPartialModal] = useState(false);
    // const [showOptions, setShowOptions] = useState(false);
    const [refundOption, setRefundOption] = useState("full"); // Default to Full Refund
    const [refundAmount, setRefundAmount] = useState(null);
    const now = moment.utc();

    // Extract booking details
    const arrivalDate = booking?.arrival_date;
    // const departureDate = booking?.departure_date;
    const checkInAfter = booking?.meta_data?.listing?.check_in_after;
    // Function to calculate total extra expenses
    const calculateTotalExtraExpenses = (booking) => {
        const { expenses, meta_data, no_of_guests } = booking;
        const totalNights = meta_data.totalDays;

        // Filter approved expenses
        const approvedExpenses = (expenses || []).filter(
            (expense) => expense.status === "approved"
        );

        // Calculate total extra expenses
        const totalExtraExpenses = approvedExpenses.reduce((total, expense) => {
            const expenseValue = parseFloat(expense.expense_value);
            const expenseQty = parseFloat(expense.expense_qty);

            switch (expense.expense_type) {
                case "Per Night":
                    return total + expenseValue * expenseQty * totalNights;
                case "Per Night Per Guest":
                    return total + expenseValue * expenseQty * totalNights * no_of_guests;
                default:
                    return total;
            }
        }, 0);

        return totalExtraExpenses.toFixed(2); // Return the total rounded to 2 decimal places
    };


    const [totalExtraExpenses, setTotalExtraExpenses] = useState(calculateTotalExtraExpenses(booking) || 0);
    // Ensure all values exist before processing
    const isBeforeCheckIn = arrivalDate && checkInAfter
        ? now.isBefore(moment.utc(`${arrivalDate} ${moment(checkInAfter, ["h:mm A"]).format("HH:mm:ss")}`, "YYYY-MM-DD HH:mm:ss"))
        : false;
    const [partialAmount, setPartialAmount] = useState(0);
    const [partialReason, setPartialReason] = useState('');
    const [type, setType] = useState(null);

    useEffect(() => {
        getPaymentMethods();
    }, [getPaymentMethods]);

    const tabs = [
        { id: "reservationDates", label: "Reservation Dates" },
        { id: "listingSummary", label: "Listing Summary" },
        { id: "paymentSummary", label: "Payment Summary" },
    ];

    const columns = [
        {
            accessorKey: 'id',
            header: 'Expense ID',
        },
        {
            accessorKey: 'expense_name',
            header: 'Expense Name',
        },
        {
            accessorKey: 'expense_value',
            header: 'Expense Value',
            Cell: ({ cell }) => `$${cell.getValue()}`,
        },
        {
            accessorKey: 'expense_qty',
            header: 'Quantity',
        },
        {
            accessorKey: 'expense_type',
            header: 'Expense Type',
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => (
                <span className={`font-bold ${cell.getValue() === 'pending' ? 'text-yellow-500' : 'text-green-500'}`}>
                    {cell.getValue()}
                </span>
            ),
        },
        // Conditionally show Actions column only if status is 'pending'
        {
            header: 'Actions',
            Cell: ({ row }) => {
                const expense = row.original; // Get row data
                if (expense.status !== 'pending') return null; // Hide actions for non-pending status

                return (
                    <div className="flex space-x-2">
                        <select
                            className="px-4 py-2 text-gray-800 bg-gray-100 border rounded-md"
                            onChange={(e) => handleAction(e, expense.id, expense)}
                        >
                            <option value="">Select Action</option>
                            <option value="accept">Accept</option>
                            <option value="reject">Reject</option>
                        </select>
                    </div>
                );
            },
        },
    ];

    const postColumns = [
        {
            accessorKey: 'id',
            header: 'Charge ID',
        },
        {
            accessorKey: 'reason',
            header: 'Reason',
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            Cell: ({ cell }) => `$${cell.getValue()}`, // Formatting the value as currency
        },
        {
            accessorKey: 'situation_description',
            header: 'Description',
        },
        {
            accessorKey: 'booking_id',
            header: 'Booking ID',
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => (
                <span className={`font-bold ${cell.getValue() === 'pending' ? 'text-yellow-500' : 'text-green-500'}`}>
                    {cell.getValue()}
                </span>
            ),
        },
        // Conditionally add the Actions column only when there is at least one pending charge
        ...(booking?.post_checkout_charges?.some((charge) => charge.status === 'pending')
            ? [
                {
                    header: 'Actions',
                    Cell: ({ row }) => {
                        const charge = row.original;
                        return (
                            <div className="flex space-x-2">
                                <select
                                    className="px-4 py-2 text-gray-800 bg-gray-100 border rounded-md"
                                    onChange={(e) => handlePostAction(e, charge.id, charge)}
                                >
                                    <option value="">Select Action</option>
                                    <option value="accept">Accept</option>
                                    <option value="reject">Reject</option>
                                </select>
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];


    // if Host
    const handleCancelBooking = useCallback(async () => {
        if (!booking?.id) {
            Swal.fire("Error", "Booking ID is required", "error");
            return;
        }

        const data = {
            bookingId: booking.id,
            meta_data: {
                ...booking.meta_data,
                rejection: [
                    ...(Array.isArray(booking.meta_data?.rejection) ? booking.meta_data.rejection : []),
                    {
                        reason: rejectionReason,
                        refundOption: isBeforeCheckIn ? refundOption : "none",
                        refundAmount: isBeforeCheckIn && refundOption === "partial" ? refundAmount : null,
                    }
                ]
            },
            rejectionReason,
            refundOption: isBeforeCheckIn ? refundOption : "none",
            refundAmount: isBeforeCheckIn && refundOption === "partial" ? refundAmount : null,

            guest_id: booking.guest.id,
            host_id: booking.listing.host_id
        };


        // Show confirmation alert before proceeding
        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to cancel this booking?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, cancel it!",
        });

        if (!confirmResult.isConfirmed) {
            return; // Stop if the user cancels the action
        }

        try {
            const response = await chatServerInstance.post('/bookings/cancel', data);

            // Show success alert
            Swal.fire("Success", "Booking canceled successfully!", "success");

            console.log("Booking canceled successfully:", response.data);
            setShowModal(false); // Close modal on success
        } catch (error) {
            console.error("Error canceling booking:", error.response?.data || error.message);

            // Show error alert
            Swal.fire("Error", error.response?.data?.message || "Something went wrong!", "error");
        }
    }, [booking.guest.id, booking.id, booking.listing.host_id, booking.meta_data, isBeforeCheckIn, refundAmount, refundOption, rejectionReason]);

    // if Guest
    const handleCancelBookingGuest = useCallback(async () => {
        if (!booking?.id) {
            Swal.fire("Error", "Booking ID is required", "error");
            return;
        }

        const data = {
            bookingId: booking.id,
            meta_data: {
                ...booking.meta_data,
                rejection: [
                    ...(Array.isArray(booking.meta_data?.rejection) ? booking.meta_data.rejection : []),
                    {
                        reason: rejectionReason,
                        refundOption: isBeforeCheckIn ? refundOption : "none",
                        refundAmount: isBeforeCheckIn && refundOption === "partial" ? refundAmount : null,
                    }
                ]
            },
            rejectionReason,
            refundOption: isBeforeCheckIn ? refundOption : "none",
            refundAmount: isBeforeCheckIn && refundOption === "partial" ? refundAmount : null,

            guest_id: booking.guest.id,
            host_id: booking.listing.host_id
        };


        // Show confirmation alert before proceeding
        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to cancel this booking?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, cancel it!",
        });

        if (!confirmResult.isConfirmed) {
            return; // Stop if the user cancels the action
        }

        try {
            const response = await chatServerInstance.post('/bookings/guest-cancel', data);

            // Show success alert
            Swal.fire("Success", "Booking canceled successfully!", "success");

            console.log("Booking canceled successfully:", response.data);
            setShowModal(false); // Close modal on success
        } catch (error) {
            console.error("Error canceling booking:", error.response?.data || error.message);

            // Show error alert
            Swal.fire("Error", error.response?.data?.message || "Something went wrong!", "error");
        }
    }, [booking.guest.id, booking.id, booking.listing.host_id, booking.meta_data, isBeforeCheckIn, refundAmount, refundOption, rejectionReason]);

    const handlePartialRefund = useCallback(async () => {
        const data = {
            bookingId: booking.id,
            meta_data: JSON.stringify({
                ...booking.meta_data,
                refund: [
                    ...(Array.isArray(booking.meta_data?.refund) ? booking.meta_data.refund : []),
                    { reason: partialReason, amount: partialAmount }
                ]
            }),
            partialAmount,
            partialReason,
            guest_id: booking.guest.id,
            host_id: booking.listing.host_id
        };


        try {
            const response = await chatServerInstance.post('/bookings/refund', data);

            Swal.fire({
                icon: "success",
                title: "Refund Successful",
                text: response.data.message, // "Partial refund successful"
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK"
            });

            setPartialModal(false);

            console.log("Refund successfully:", response.data);
        } catch (error) {
            console.error("Refund Failed:", error.response?.data || error.message);

            Swal.fire({
                icon: "error",
                title: "Refund Failed",
                text: error.response?.data?.message || "Something went wrong. Please try again.",
                confirmButtonColor: "#d33",
                confirmButtonText: "Close"
            });
        }
    }, [booking, partialAmount, partialReason]);

    const processPostCheckoutCharge = async (chargeId, action) => {
        try {
            // Show loading Swal
            Swal.fire({
                text: 'Processing Post Checkout Charges...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await chatServerInstance.post('/expenses/process-post-checkout', {
                chargeId,
                status: action,
                customer: user?.payment_customer_id,
                userId: user?.id,
                paymentMethodId: selectedPaymentMethod,

                guest_id: booking.guest.id,
                host_id: booking.listing.host_id
            });

            if (!response || !response.data) {
                throw new Error('Invalid response from server.');
            }

            const result = response.data;
            console.log('Checkout Expenses Processed:', result);

            if (!result.success) {
                throw new Error(result.message || 'Failed to process charge.');
            }

            // Success alert
            Swal.fire({
                icon: 'success',
                text: 'Post Checkout Charge Successful!.',
            });
            window.location.reload();
        } catch (error) {
            console.error('Error processing charge.:', error);

            // Error alert
            Swal.fire({
                icon: 'error',
                text: error.message || 'Something went wrong. Please try again.',
            });
        }
    }

    const handlePostAction = async (e, chargeId) => {
        const action = e.target.value;
        if (action === 'accept') {
            toggleModal('post');
        } else if (action === 'reject') {
            processPostCheckoutCharge(chargeId, 'rejected');
        }
        // window.location.reload();
    };

    // Action handler
    const handleAction = (e, expenseId, expense) => {
        const action = e.target.value;
        if (action === 'accept') {
            handleAccept(expenseId, expense); // Call accept handler with expense data
        } else if (action === 'reject') {
            handleReject(expenseId); // Call reject handler
        }
        // window.location.reload();
    };

    // Accept handler
    const handleAccept = (expenseId, expense) => {
        const totalGuests = booking?.no_of_guests + booking?.no_of_childrens + booking?.no_of_infants;
        const totalNights = diffDays; // Assuming diffDays is the total number of nights

        let totalPrice = 0;
        const expenseValue = parseFloat(expense.expense_value);
        const expenseQty = parseFloat(expense.expense_qty);

        // Calculate price based on expense type
        switch (expense.expense_type) {
            case 'Single Fee':
                totalPrice = expenseValue * expenseQty;
                break;
            case 'Per Night':
                totalPrice = expenseValue * expenseQty * totalNights;
                break;
            case 'Per Guest':
                totalPrice = expenseValue * expenseQty * totalGuests;
                break;
            case 'Per Night Per Guest':
                totalPrice = expenseValue * expenseQty * totalNights * totalGuests;
                break;
            default:
                console.log('Unknown expense type');
                return;
        }

        console.log(`Accepted Expense ID: ${expenseId}`);
        console.log(`Total Price for Expense: $${totalPrice.toFixed(2)}`);

        // Store accepted expenses in state
        setAcceptedExpenses(prev => [...prev, { expenseId, totalPrice }]);

        // Update UI total
        setTotalExtraExpenses(exp => parseFloat(exp) + totalPrice);
    };

    // Reject handler
    const handleReject = (expenseId) => {
        setRejectedExpenses(exp => [...exp, expenseId])
        console.log('Rejected Expense ID:', expenseId);
        // Handle rejection logic here, such as updating the expense status
    };


    const toggleModal = (type) => {
        setType(type)
        setIsModalOpen(!isModalOpen);
    }


    const handleConfirmExtraExpenses = async () => {
        try {
            console.log('Confirming Extra Expenses:', acceptedExpenses);

            // Validate input fields
            if (!acceptedExpenses || acceptedExpenses.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    text: 'Please select expenses to confirm.',
                });
                return;
            }

            if (!selectedPaymentMethod) {
                Swal.fire({
                    icon: 'warning',
                    text: 'Please select a payment method before proceeding.',
                });
                return;
            }

            if (!user?.payment_customer_id || !user?.id) {
                Swal.fire({
                    icon: 'error',
                    text: 'User or customer ID is missing. Please try logging in again.',
                });
                return;
            }

            const data = {
                acceptedExpenses,
                paymentMethodId: selectedPaymentMethod,
                customerId: user.payment_customer_id,
                userId: user.id,

                guest_id: booking.guest.id,
                host_id: booking.listing.host_id
            };

            // Show loading Swal
            Swal.fire({
                text: 'Please wait while we confirm the expenses.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await chatServerInstance.post('/expenses/accept', data);

            if (!response || !response.data) {
                throw new Error('Invalid response from server.');
            }

            const result = response.data;
            console.log('Accepted Expenses Processed:', result);

            if (!result.success) {
                throw new Error(result.message || 'Payment processing failed.');
            }

            // Check for payment intent errors
            if (result.paymentIntent) {
                const { status, next_action, last_payment_error } = result.paymentIntent;

                if (status !== 'succeeded') {
                    throw new Error(`Payment failed with status: ${status}`);
                }

                if (next_action) {
                    console.warn('Payment requires further action:', next_action);
                }

                if (last_payment_error) {
                    throw new Error(`Payment error: ${last_payment_error.message}`);
                }
            }


            // Show success alert
            Swal.fire({
                icon: 'success',
                text: 'Your expenses have been successfully confirmed and paid.',
            });

            // Clear accepted expenses after successful update
            setAcceptedExpenses([]);
            window.location.reload();
        } catch (error) {
            console.error('Error processing accepted expenses:', error);

            // Show error alert
            Swal.fire({
                icon: 'error',
                text: error.message || 'Something went wrong. Please try again.',
            });
        }
    };


    const handleRejectExtraExpenses = async () => {
        try {
            console.log('Rejecting Extra Expenses:', rejectedExpenses);

            // Validate rejected expenses
            if (!rejectedExpenses || rejectedExpenses.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    text: 'Please select expenses to reject.',
                });
                return;
            }

            const data = {
                rejectedExpenses,
                guest_id: booking.guest.id,
                host_id: booking.listing.host_id
            };
            console.log('Request Data:', data);

            // Show loading Swal
            Swal.fire({
                text: 'Rejecting selected expenses...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await chatServerInstance.post('/expenses/reject', data);

            if (!response || !response.data) {
                throw new Error('Invalid response from server.');
            }

            const result = response.data;
            console.log('Rejected Expenses Processed:', result);

            if (!result.success) {
                throw new Error(result.message || 'Failed to reject expenses.');
            }

            // Success alert
            Swal.fire({
                icon: 'success',
                text: 'The selected expenses have been successfully rejected.',
            });

            // Clear rejected expenses after successful update
            setRejectedExpenses([]);
            window.location.reload();
        } catch (error) {
            console.error('Error rejecting expenses:', error);

            // Error alert
            Swal.fire({
                icon: 'error',
                text: error.message || 'Something went wrong. Please try again.',
            });
        }
    };

    console.log(booking)

    const renderContent = () => {
        switch (activeTab) {
            case "listingSummary":
                return (
                    <div>
                        <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Listing Summary</h3>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Listing Title </h4>
                            <p className="text-sm !m-0">{booking?.listing?.title || ""}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Check In</h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.check_in_after || ''}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Check Out</h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.check_out_before || ''}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0  flex items-center"> <PiGreaterThanLight className="mr-3" />Smoking allowed: </h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.smoking_allowed === 1 ? "Yes" : "No"}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0  flex items-center"> <PiGreaterThanLight className="mr-3" />Pets allowed: </h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.pet_allowed === 1 ? "Yes" : "No"}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0 flex items-center"> <PiGreaterThanLight className="mr-3" />Party allowed: </h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.party_allowed === 1 ? "Yes" : "No"}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0  flex items-center"> <PiGreaterThanLight className="mr-3" />Children allowed: </h4>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.children_allowed === 1 ? "Yes" : "No"}</p>
                        </div>


                        <div className="flex flex-col justify-between gap-3 mt-2 text-gray-800">
                            <h3 className="font-semibold text-gray-900 border-b text-md">House Rules / Additional Information</h3>
                            <p className="text-sm !m-0">{booking?.meta_data?.listing?.rules || "No House Rules"}</p>
                            <h4 className="text-sm font-medium !m-0  flex items-center">Long Term Cancellation Policies </h4>
                            <ol className="flex flex-col gap-3">
                                <li className="text-sm !m-0"> {booking?.meta_data?.listing?.cancellation_policy_long?.after_check_in}</li>
                                <li className="text-sm !m-0"> {booking?.meta_data?.listing?.cancellation_policy_long?.before_check_in}</li>
                            </ol>
                            <h4 className="text-sm font-medium !m-0  flex items-center"> Short Term Cancellation Policies </h4>
                            <ol className="flex flex-col gap-3">
                                <li className="text-sm !m-0">{booking?.meta_data?.listing?.cancellation_policy_short?.after_check_in}</li>
                                <li className="text-sm !m-0">{booking?.meta_data?.listing?.cancellation_policy_short?.before_check_in}</li>
                            </ol>
                        </div>

                        <div className="flex flex-col justify-between gap-3 mt-2 text-gray-800">
                            <h4 className="text-sm font-semibold !m-0 flex items-center">Please refer to the email sent to you by Fly-Inn for Welcome Instructions</h4>
                        </div>
                    </div>
                );
            case "reservationDates":
                return (
                    <div>
                        <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Booking Status</h3>

                        <div className="flex flex-col items-center">
                            <div className="flex justify-between w-full">
                                <h4 className="text-sm font-medium !m-0">Booking ID </h4>
                                <p className="text-sm !m-0">{booking?.id}</p>
                            </div>
                            <div className="flex justify-between w-full">
                                <h4 className="text-sm font-medium !m-0">Booking Status </h4>
                                <p className="text-sm !m-0">
                                    <button
                                        className={`shadow-md rounded ${getBookingStatusAndStyles(booking?.status).buttonColor} ${getBookingStatusAndStyles(booking?.status).textColor}`}
                                        style={{ padding: "7px 10px" }}
                                    >
                                        {getBookingStatusAndStyles(booking?.status).label}
                                    </button>

                                </p>
                            </div>
                        </div>
                        <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Booking Summary</h3>
                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Number of Nights </h4>
                            <p className="text-sm !m-0">{diffDays}</p>
                        </div>


                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Number of Guests (Adults)</h4>
                            <p className="text-sm !m-0">{parseInt(booking?.no_of_guests) || 0}</p>
                        </div>

                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Number of Guests (Children / Infants)</h4>
                            <p className="text-sm !m-0">{parseInt(booking?.no_of_childrens) + parseInt(booking?.no_of_infants)}</p>
                        </div>
                        <h3 className="pb-2 font-semibold border-b text-md">Guest Details</h3>
                        <div className="mt-4">
                            <div className="flex justify-between">
                                <h4>Name:</h4>
                                <p>{booking?.guest?.display_name}</p>
                            </div>
                        </div>
                        <h3 className="pb-2 font-semibold border-b text-md">Host Details</h3>
                        <div className="mt-4">
                            <div className="flex justify-between">
                                <h4>Name:</h4>
                                <p>{booking?.listing?.host?.display_name}</p>
                            </div>
                        </div>
                        <h3 className="pb-2 font-semibold border-b text-md">Reservation Dates</h3>
                        <div className="mt-4">
                            <div className="flex justify-between">
                                <h4>Arrival:</h4>
                                <p>{arrival}</p>
                            </div>
                            <div className="flex justify-between">
                                <h4>Departure:</h4>
                                <p>{departure}</p>
                            </div>
                        </div>
                    </div>
                );
            case "paymentSummary":
                return (
                    <div>
                        <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Payment Summary</h3>
                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Nightly Price </h4>
                            <p className="text-sm !m-0">${booking?.meta_data?.listing_rates?.nightly}</p>
                        </div>
                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Cleaning Fee</h4>
                            <p className="text-sm !m-0">${booking?.meta_data?.totalCleaningFee}</p>
                        </div>
                        {parseInt(booking?.meta_data?.totalCityFee) > 0 && (
                            <div className="flex justify-between mt-1 text-gray-800">
                                <h4 className="text-sm font-medium !m-0">City Fee</h4>
                                <p className="text-sm !m-0">${booking?.meta_data?.totalCityFee}</p>
                            </div>
                        )
                        }
                        <div className="flex justify-between mt-1 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Platform Fee</h4>
                            <p className="text-sm !m-0">${booking?.meta_data?.platformFee}</p>
                        </div>
                        {booking?.meta_data?.petsPrice > 0 && (
                            <div className="flex justify-between mt-1 text-gray-800">
                                <h4 className="text-sm font-medium !m-0">Pets Fee</h4>
                                <p className="text-sm !m-0">${booking?.meta_data?.petsPrice}</p>
                            </div>
                        )}
                        {booking?.meta_data?.additionalGuestsQuantity > 0 &&
                            (
                                <div>
                                    <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Additional Guests</h3>
                                    <div className="flex justify-between mt-2 text-gray-800">
                                        <h4 className="text-sm font-medium !m-0">Number of Additional Guests</h4>
                                        <p className="text-sm !m-0">{booking?.meta_data?.additionalGuestsQuantity}</p>
                                    </div>
                                    <div className="flex justify-between mt-1 text-gray-800">
                                        <h4 className="text-sm font-medium !m-0">Additional Guests Charge</h4>
                                        <p className="text-sm !m-0">${booking?.meta_data?.additionalGuestsPrice}</p>
                                    </div>
                                </div>
                            )}
                        {booking?.meta_data?.extraServicesPrice > 0 && (
                            <div>
                                <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Extra Services</h3>
                                <div className="flex justify-between mt-2 text-gray-800">
                                    <h4 className="text-sm font-medium !m-0">Extra Services Price</h4>
                                    <p className="text-sm !m-0">${booking?.meta_data?.extraServicesPrice}</p>
                                </div>

                            </div>
                        )}

                        {booking?.expenses && booking?.expenses.length > 0 &&
                            (
                                <div className="mt-4">
                                    <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Extra Expenses Summary</h3>
                                    <MaterialReactTable
                                        columns={columns}
                                        data={booking?.expenses}
                                        enableColumnResizing
                                        enableSorting
                                        enableGlobalFilter
                                        enablePagination
                                        manualPagination
                                    />

                                </div>
                            )
                        }

                        {booking?.post_checkout_charges && booking?.post_checkout_charges.length > 0 &&
                            (
                                <div className="mt-4">
                                    <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Post Checkout Charges Summary</h3>
                                    <MaterialReactTable
                                        columns={postColumns}
                                        data={booking?.post_checkout_charges}
                                        enableColumnResizing
                                        enableSorting
                                        enableGlobalFilter
                                        enablePagination
                                        manualPagination
                                    />

                                </div>
                            )
                        }
                        {booking?.meta_data?.refund?.length > 0 && (
                            <div>
                                <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Refunds</h3>
                                {booking.meta_data.refund.map((item, index) => (
                                    <div key={index} className="flex justify-between mt-2 text-gray-800">
                                        <h4 className="text-sm font-medium !m-0">Reason</h4>
                                        <p className="text-sm !m-0">{item.reason}</p>
                                    </div>
                                ))}
                                {booking.meta_data.refund.map((item, index) => (
                                    <div key={`amount-${index}`} className="flex justify-between mt-1 text-gray-800">
                                        <h4 className="text-sm font-medium !m-0">Amount</h4>
                                        <p className="text-sm !m-0">${item.amount}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {booking?.meta_data?.rejection?.length > 0 && (
                            <div>
                                <h3 className="pb-2 font-semibold text-gray-900 border-b text-md">Rejections</h3>
                                {booking.meta_data.rejection.map((item, index) => (
                                    <div key={index} className="flex justify-between mt-2 text-gray-800">
                                        <h4 className="text-sm font-medium !m-0">Reason</h4>
                                        <p className="text-sm !m-0">{item.reason}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h3 className="pb-2 mt-4 font-semibold text-gray-900 border-b text-md">Summary
                            {
                                booking?.expenses && booking?.expenses.length > 0 && !isHost &&
                                <span>
                                    (all total and taxes are excluding extra expenses)
                                </span>
                            }
                        </h3>
                        <div className="flex justify-between mt-2 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Total before taxes</h4>
                            <p className="text-sm !m-0">${booking?.meta_data?.totalAmount}</p>
                        </div>
                        <div className="flex justify-between mt-1 text-gray-800">
                            <h4 className="text-sm font-medium !m-0">Taxes</h4>
                            <p className="text-sm !m-0">${booking?.meta_data?.taxAmount}</p>
                        </div>
                        {
                            booking?.expenses && booking?.expenses.length > 0 && !isHost &&
                            <div className="flex justify-between mt-1 text-gray-800">
                                <h4 className="text-sm font-medium !m-0">Accepted Extra Expenses by Guest</h4>
                                <p className="text-sm !m-0">${totalExtraExpenses}</p>
                            </div>
                        }
                        <div className="flex justify-between mt-1 font-semibold text-gray-800">
                            <h4 className="text-base !m-0">Total</h4>
                            <p className="text-base !m-0">${booking?.meta_data?.amountAfterTax}</p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };


    return (
        <div className="container mx-auto">
            {/* Tabs for larger screens */}
            <div className="flex-wrap hidden bg-white rounded-md sm:flex">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`flex-grow px-4 py-2 font-bold text-lg border hover:bg-red-700 hover:text-white ${activeTab === tab.id
                            ? "text-white  bg-gray-400"
                            : "text-gray-500"
                            }`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Dropdown for smaller screens */}
            <div className="mb-6 sm:hidden">
                <select
                    className="w-full p-2 border rounded-md"
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                >
                    {tabs.map((tab) => (
                        <option key={tab.id} value={tab.id}>
                            {tab.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Tab content */}
            <div className="p-5 bg-white border-t rounded-md shadow-md">{renderContent()}</div>

            {booking.status !== 'CANCELLED' && (
                <>
                    {isDeclined && (
                        <div className="sticky bottom-0 flex justify-center w-full p-5 text-white bg-red-500 rounded-md shadow-md">
                            <h3 className="text-xl text-center text-white">The Host has cancelled this booking, {booking?.meta_data?.rejection_reason || ''}</h3>
                        </div>
                    )}

                    {/* Confirmation/Action buttons */}
                    {!isConfirm && isHost && !isDeclined && (
                        <div className="sticky bottom-0 flex justify-end w-full p-5 bg-white rounded-md shadow-md">
                            <button
                                className="bg-[#8ec639] text-white mx-4 px-4 py-2 rounded-md"
                                onClick={() => handleConfirmBooking(1, rejectionReason, booking?.meta_data)}
                            >
                                Confirm Booking
                            </button>
                            <button
                                className="px-4 py-2 mx-4 text-white bg-red-700 rounded-md"
                                onClick={() => {
                                    setIsDeclined(true)
                                    setShowModal(true)
                                }} // Open modal
                            >
                                Decline
                            </button>
                        </div>
                    )}

                    {/* Cancel button */}
                    {isConfirm && isHost && (
                        <div className="sticky bottom-0 flex flex-wrap justify-end w-full gap-3 p-5 bg-white rounded-md shadow-md">

                            {/* {(booking.status === 'CHECK_IN') &&
                        <button
                            className="bg-[#8ec639] text-white px-4 py-2 rounded"
                            onClick={() => toggleStatus('COMPLETED')}
                        >
                            Check Out
                        </button>
                    } */}

                            <button
                                className="px-4 py-2 text-white bg-gray-400 rounded"
                                onClick={() => setExpenses(true)}
                            >
                                Extra Expenses
                            </button>
                            {/* <button
                        className="px-4 py-2 text-white bg-gray-400 rounded"
                        onClick={() => setOpenDiscount(true)}
                    >
                        Discount
                    </button> */}
                            <button
                                className="px-4 py-2 text-white bg-blue-400 rounded"
                                onClick={() => setOPenPost(true)}
                            >
                                Post-Checkout Charges
                            </button>
                            <button
                                className="px-4 py-2 text-white bg-yellow-700 rounded"
                                onClick={() => setPartialModal(true)}
                            >
                                Partial Refund
                            </button>
                            {isHost &&
                                <button
                                    className="px-4 py-2 text-white bg-blue-700 rounded"
                                    onClick={() =>
                                        handleConfirmBooking("COMPLETED", 'COMPLETED', booking?.meta_data)
                                    }
                                >
                                    Complete Booking
                                </button>
                            }
                            <button
                                className="px-4 py-2 text-white bg-red-700 rounded"
                                onClick={() => setShowModal(true)}
                            >
                                Cancel Booking
                            </button>
                        </div>
                    )}

                    {/* Cancel button */}
                    {!isConfirm && !isHost && (
                        <div className="sticky bottom-0 flex flex-wrap justify-end w-full gap-3 p-5 bg-white rounded-md shadow-md">
                            {
                                acceptedExpenses?.length > 0 &&
                                <button
                                    className="px-4 py-2 text-white bg-[#8Ec639] rounded"
                                    onClick={() => toggleModal('expenses')}
                                >
                                    Confirm Extra Expenses
                                </button>
                            }

                            {
                                rejectedExpenses?.length > 0 &&
                                <button
                                    className="px-4 py-2 text-white bg-red-500 rounded"
                                    onClick={() => handleRejectExtraExpenses()}
                                >
                                    Confirm Rejection Extra Expenses
                                </button>
                            }

                            <button
                                className="px-4 py-2 text-white bg-red-700 rounded"
                                onClick={() => setShowModal(true)}
                            >
                                Cancel Booking
                            </button>
                        </div>
                    )}

                    {partialModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg w-[400px]">
                                <h2 className="mb-4 text-lg font-semibold">Partial Refund</h2>
                                <label className="block mb-2 text-gray-700">Enter the Amount:</label>
                                <input type="number"
                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 "
                                    defaultValue={partialAmount}
                                    onChange={(e) => setPartialAmount(e.target.value)}
                                    placeholder="Enter amount to refund..."
                                />
                                <textarea
                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 "
                                    rows="3"
                                    defaultValue={partialReason}
                                    onChange={(e) => setPartialReason(e.target.value)}
                                    placeholder="Enter the reason for refund..."
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        className="px-4 py-2 mr-2 text-white bg-gray-400 rounded-md"
                                        onClick={() => setPartialModal(false)}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="px-4 py-2 text-white bg-[#8Ec639] rounded-md"
                                        onClick={handlePartialRefund}
                                    >
                                        Confirm Refund
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Rejection Modal */}
                    {showModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg w-[400px]">
                                <h2 className="mb-4 text-lg font-semibold">Cancel Booking</h2>
                                <label className="block mb-2 text-gray-700">Reason for Cancellation:</label>
                                <textarea
                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                                    rows="3"
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter rejection reason..."
                                />

                                {/* Show Refund Options ONLY if cancellation is before check-in */}
                                {!isBeforeCheckIn && isHost && (
                                    <>
                                        <label className="block mt-4 text-gray-700">Select Refund Option:</label>
                                        <select
                                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            value={refundOption}
                                            onChange={(e) => setRefundOption(e.target.value)}
                                        >
                                            <option value="full">Full Refund</option>
                                            <option value="partial">Partial Refund</option>
                                            <option value="none">No Refund</option>
                                        </select>

                                        {/* Show refund amount input if Partial Refund is selected */}
                                        {refundOption === "partial" && (
                                            <div className="mt-3">
                                                <label className="block text-gray-700">Refund Amount:</label>
                                                <input
                                                    type="number"
                                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    value={refundAmount}
                                                    onChange={(e) => setRefundAmount(e.target.value)}
                                                    placeholder="Enter amount"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="flex justify-end mt-4">
                                    <button
                                        className="px-4 py-2 mr-2 text-white bg-gray-400 rounded-md"
                                        onClick={() => setShowModal(false)}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="px-4 py-2 text-white bg-red-700 rounded-md"
                                        onClick={isHost && !declined ? handleCancelBooking : isHost && declined ? () => {

                                            handleConfirmBooking(0, rejectionReason, booking?.meta_data)
                                        } : handleCancelBookingGuest}
                                    >
                                        Cancel Booking
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {isModalOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
                            <div className="relative w-full max-w-lg p-6 bg-white rounded-lg shadow-lg">
                                <div className='flex justify-end '>
                                    <i className="px-2 py-1 text-white bg-gray-300 rounded-full cursor-pointer fas fa-times" onClick={() => setIsModalOpen(false)}></i>
                                </div>
                                <>
                                    <h2 className="mb-4 text-2xl font-bold border-b">How Would you like to Pay!</h2>
                                    {/* <h4 className="p-4 mb-4 text-lg font-bold text-center bg-yellow-300 border-b rounded-md shadow-lg">Was $99, Now is $49</h4> */}

                                    <div className="space-y-2 h-[300px] overflow-scroll">
                                        {paymentMethod?.length > 0 ? (
                                            <ul>
                                                {paymentMethod.map(
                                                    (method) =>
                                                        method.type === "card" && (
                                                            <li
                                                                key={method.id}
                                                                className={`flex justify-between items-center mb-2 p-3 border border-b ${selectedPaymentMethod === method?.id && 'bg-gray-200'}`}
                                                            >
                                                                <div className={`flex items-center `}>
                                                                    <input
                                                                        type="radio"
                                                                        name="selectedPaymentMethod"
                                                                        id={`payment-method-${method.id}`}
                                                                        value={method.id}
                                                                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                                                        className="!mr-4"
                                                                    />
                                                                    <label htmlFor={`payment-method-${method.id}`} className="flex items-center">
                                                                        <FaCreditCard className="inline mr-2" />
                                                                        **** **** **** {method?.card?.last4} - {method?.billing_details?.name}
                                                                    </label>
                                                                </div>
                                                            </li>
                                                        )
                                                )}
                                            </ul>
                                        ) : (
                                            <h3 className="mb-2 text-xl font-semibold text-left">No Payment Method Found!</h3>
                                        )}
                                    </div>
                                    <div className='flex justify-end'>
                                        <button
                                            onClick={() => type === 'post' ? processPostCheckoutCharge(booking?.post_checkout_charges[0]?.id, 'approved') : handleConfirmExtraExpenses()}
                                            className="bg-[#8ec639] hover:bg-[#7fb134] px-4 py-2 mt-4 text-white rounded-md text-md font-bold"
                                        >
                                            Confirm
                                        </button>
                                    </div>
                                </>

                            </div>
                        </div>
                    )}
                </>
            )
            }

        </div >

    );
};

export default BookingDetailsTabs;
